/**
 * PSTUDIO-2871 — ProStudio node auto-numbering algorithm (Edan's ruleset v1).
 *
 * Kept pure and dependency-light (mirrors paste-node-numbering.ts / table-
 * clipboard.ts: no store/MobX imports) so the node-env vitest suite can cover
 * every branch without the heavy store.
 *
 * Returns an ordered array of `count` unique positive node numbers, guaranteed
 * not to collide with any existing node. New nodes are numbered relative to the
 * node directly ABOVE the insertion point (`reference`); `upper` is the node
 * directly below it (null when appending at the end / no lower neighbour).
 *
 * PSTUDIO-4083 — the two number sets are SEPARATE arguments, and the separation
 * is the whole point of the fifth one. `existingNodes` are the numbers rows
 * actually HOLD; `referencedNodes` are numbers some row merely POINTS AT. Both
 * block a collision — that is rule 1.6, and handing a new node a number a
 * dangling edge still names is what it forbids — but only the first may set the
 * `max` Fallback counts up from. Folding them into one set (which this function
 * did when the rule was first written) made a reference decide where numbering
 * STARTS: real solutions ship `error~99990` in `whatsNext` (see `src/output.json`
 * and `resource-lib/templates.ts`), so one "Insert above" over the first row
 * answered 99991 and every later append counted on from there. A reference is
 * evidence that a number is spoken for; it is not evidence about how big the
 * solution is.
 */
// eslint-disable-next-line max-params
export function assignNodeNumbers(
  reference: number | null,
  upper: number | null,
  count: number,
  existingNodes: readonly number[],
  referencedNodes: readonly number[] = []
): number[] {
  const existing = new Set(existingNodes);
  // The Fallback anchor is read BEFORE the referenced numbers join the set, so
  // it can only ever be a number a row holds.
  let maxOwned = 0;
  for (const n of existing) if (n > maxOwned) maxOwned = n;
  referencedNodes.forEach((n) => existing.add(n));

  // Rule 1: normalize a negative reference to its positive counterpart.
  let ref = reference;
  if (ref !== null && ref < 0) ref = Math.abs(ref);

  // PSTUDIO-3992: a neighbour beyond Number.MAX_SAFE_INTEGER is not a usable
  // anchor, and letting one in hangs the allocator two different ways: `n++`
  // stops advancing (sequentialFill / fallback), and `String(1e21)` is "1e+21",
  // which parseInt reads as 1 — so familyExpansion's base never grows past its
  // 6-digit cap and the depth loop never exits. Guarding at the entry point
  // covers every strategy at once, so no caller has to sanitise for us. A row
  // numbered that high keeps its number everywhere else (guard rail 11); it
  // just does not get a vote on where the NEXT number goes.
  //
  // PSTUDIO-4083: `0` is rejected here for the same reason and by the same
  // line. Both expansions derive their base by APPENDING zeros to the
  // reference, and `parseInt('0' + '0'.repeat(k))` is `0` for every k — so the
  // base never grows, the block of candidates never changes, and
  // standardExpansion's depth loop spins forever (measured: a 5s watchdog kill
  // on `assignNodeNumbers(0, null, 2, [1, 2])`). `-0` reaches the same place:
  // `-0 < 0` is false, so Rule 1's Math.abs does not fire, and `String(-0)` is
  // "0". No production caller can produce it today — every one of them goes
  // through `toValidNodeNumber`, which requires `> 0` — but that guard lives in
  // other files, and this function normalises a negative reference itself, so
  // it already accepts input it was not handed. A reference of 0 is not an
  // anchor any child can be derived from; Fallback is the honest answer.
  if (ref !== null && (!Number.isSafeInteger(ref) || ref <= 0))
    return fallback(count, existing, maxOwned);
  const safeUpper =
    upper !== null && Number.isSafeInteger(upper) ? upper : null;

  // No reference node (inserting above the very first node) → Fallback.
  if (ref === null) return fallback(count, existing, maxOwned);

  // Family Mode: upper is a descendant of reference (shares leading digits).
  if (safeUpper !== null && String(safeUpper).startsWith(String(ref))) {
    return familyExpansion(ref, safeUpper, count, existing, maxOwned);
  }

  // Try Sequential Fill (lexicographic upper-boundary check).
  const seq = sequentialFill(ref, safeUpper, count, existing);
  if (seq !== null) return seq;

  // Standard Expansion (always succeeds).
  return standardExpansion(ref, count, existing, maxOwned);
}

/**
 * PSTUDIO-4453: the allocator as rows see it. Rows hold canonical strings; the
 * algorithm above counts in JS numbers, which is exact over its whole input
 * domain — positive safe integers, the only numbers `toValidNodeNumber` lets
 * in — so the strings are made here, once, rather than threaded through every
 * strategy.
 */
// eslint-disable-next-line max-params
export function assignNodeNumberStrings(
  reference: number | null,
  upper: number | null,
  count: number,
  existingNodes: readonly number[],
  referencedNodes: readonly number[] = []
): string[] {
  return assignNodeNumbers(
    reference,
    upper,
    count,
    existingNodes,
    referencedNodes
  ).map(String);
}

/**
 * Takes the CONTIGUOUS block of `count` numbers starting at reference+1, or
 * nothing. Returns null — signalling Expansion should answer instead — as soon
 * as a candidate is already taken, or reaches/exceeds `upper` lexicographically.
 *
 * PSTUDIO-4083: the block used to be gap-filled, counting PAST taken numbers.
 * That is what made an insert under node 5 of a `1 … 10` table answer 11: 6-10
 * were skipped and 11 is lexicographically below `upper` 6. Edan's sign-off
 * (2026-09-08, Confluence 5744001026) states a new node takes a NET NEW number
 * "to be considered the 'children' of the node immediately above" — 11 reads as
 * a child of node 1, 50 reads as a child of node 5. Refusing on the first
 * conflict is what hands the run to Standard Expansion, which produces 50.
 *
 * Deliberately the same shape `familyExpansion` has had since PSTUDIO-2871
 * ("Accept only if the entire block is conflict-free — no gap-filling"); the
 * two strategies had simply disagreed about it.
 *
 * A sparse table — the step of 10 real solutions use — is NOT pushed into
 * Expansion by this: `10 · 20 · 30 · 40` inserting under 20 still answers 21,
 * because 21 is free. Only a densely numbered neighbourhood hands over.
 */
// eslint-disable-next-line max-params
function sequentialFill(
  reference: number,
  upper: number | null,
  count: number,
  existing: ReadonlySet<number>
): number[] | null {
  const candidates: number[] = [];

  for (let n = reference + 1; candidates.length < count; n++) {
    // PSTUDIO-3992: past Number.MAX_SAFE_INTEGER `n++` stops advancing (float
    // precision). The block is contiguous now, so this can no longer spin — but
    // it would emit the same number twice, so the hand-over stays.
    if (n > Number.MAX_SAFE_INTEGER) return null;
    // PSTUDIO-4083: a taken candidate ends the run instead of being skipped —
    // see the docblock. The block is all-or-nothing.
    if (existing.has(n)) return null;
    // Lexicographic comparison — e.g. "301" < "31" is true ('0' < '1'),
    // so inserting between 300 and 31 correctly yields 301, 302, 303.
    if (upper !== null && String(n) >= String(upper)) {
      return null; // reached/exceeded upper — use Expansion instead.
    }
    candidates.push(n);
  }

  return candidates;
}

/**
 * Appends one or two zeros to `reference` to derive an expansion base, then
 * takes the CONTIGUOUS block of `count` numbers from it. count 1–9 → "0"
 * (32 → 320); 10–99 → "00" (32 → 3200). Any conflict inside the block appends
 * one more zero and retries a digit deeper.
 *
 * PSTUDIO-4083: the block used to be gap-filled, scanning upward and skipping
 * taken numbers. That is the second half of the "a number goes missing when I
 * add ten nodes" report from the 2026-09-08 call — one taken number anywhere
 * inside the block punched a hole through the run, so a single gesture produced
 * numbers that no longer read as one sequence. Measured before this change:
 * `assignNodeNumbers(4, null, 10, [1, 8, 2, 4, 405])` answered
 * `400..404, 406..410`, skipping the taken 405; it now answers `4000..4009`.
 *
 * Deliberately the same depth-jump `familyExpansion` has had since PSTUDIO-2871
 * ("Accept only if the entire block is conflict-free — no gap-filling"), which
 * Edan and Leo re-confirmed as intended on 2026-08-05 (§6 · §7). For `count > 1`
 * all three strategies now agree about what a conflict means.
 *
 * Scoped to `count > 1` ON PURPOSE, and that scope is a real difference, not a
 * rounding of one: for a SINGLE node `singleExpansion` counts up past taken
 * numbers while `familyExpansion` would jump a digit, so the two disagree about
 * a conflict in exactly that case. It buys the shorter number. The defect this
 * ticket is about is a RUN with a hole in it, and a single node is a run of one
 * — nothing can break it, so a taken base there only means "take the next free
 * number", which is still a child of `reference` (inserting one node under `1`
 * with `10..18` taken answers `19`, and `19` reads as a child of `1` exactly as
 * `100` would, while being shorter). Jumping a digit for a single node would
 * deepen every ordinary one-node insert with no defect to show for it. The cost
 * of the split is that two gestures reaching the same slot can answer at
 * different depths — one `Add node` twice gives `11, 12` where one `Add 2
 * nodes` gives `100, 101`. Recorded in §5 as open with product.
 *
 * None of the sixteen acceptance cases of §3 moves: checked case by case, not
 * one of them has a taken number inside its expansion block.
 */
// eslint-disable-next-line max-params
function standardExpansion(
  reference: number,
  count: number,
  existing: ReadonlySet<number>,
  maxOwned: number
): number[] {
  const zeros = count <= 9 ? 1 : 2;

  if (count === 1) return singleExpansion(reference, zeros, existing, maxOwned);

  for (let depth = 0; ; depth++) {
    const base = parseInt(String(reference) + '0'.repeat(zeros + depth), 10);
    // PSTUDIO-2871 / PSTUDIO-3992: symmetry with familyExpansion's 6-digit cap
    // and fallback — past Number.MAX_SAFE_INTEGER arithmetic stops being exact,
    // so a block derived up here would emit duplicates. Fall back to max+1.
    // Testing the block's LAST number covers the base too (`count >= 1` on every
    // path that reaches this function, because sequentialFill answers `[]` for
    // `count === 0` before Expansion is ever consulted), so the separate
    // `base > MAX_SAFE_INTEGER` check that used to stand here was removable
    // rather than redundant-but-harmless: it was unkillable by mutation, which
    // is how it was found.
    //
    // This loop terminates because `base` grows tenfold per depth — which holds
    // only because `reference >= 1`. It is `assignNodeNumbers` that guarantees
    // that, by sending `0` to Fallback at the entry point; appending zeros to a
    // reference of `0` would leave the base at `0` forever.
    if (base + count - 1 > Number.MAX_SAFE_INTEGER)
      return fallback(count, existing, maxOwned);

    const candidates = Array.from({ length: count }, (_, i) => base + i);
    // Accept only if the entire block is conflict-free — no gap-filling.
    if (candidates.every((n) => !existing.has(n))) return candidates;
    // Any conflict → jump a whole digit, exactly as familyExpansion does.
  }
}

/**
 * The one-node case of Standard Expansion: count up from the expansion base,
 * skipping taken numbers. This is the pre-PSTUDIO-4083 loop, kept verbatim for
 * a single node — see the note on `standardExpansion` for why a run of one
 * cannot have the hole this ticket is about.
 */
// eslint-disable-next-line max-params
function singleExpansion(
  reference: number,
  zeros: number,
  existing: ReadonlySet<number>,
  maxOwned: number
): number[] {
  const base = parseInt(String(reference) + '0'.repeat(zeros), 10);
  if (base > Number.MAX_SAFE_INTEGER) return fallback(1, existing, maxOwned);

  for (let n = base; ; n++) {
    // PSTUDIO-3992: past Number.MAX_SAFE_INTEGER `n++` stops advancing, so the
    // scan would emit the same number forever. Hand over to the fallback.
    if (n > Number.MAX_SAFE_INTEGER) return fallback(1, existing, maxOwned);
    if (!existing.has(n)) return [n];
  }
}

/**
 * Used when `upper` is a descendant of `reference`. Starting one digit deeper
 * than `upper`, checks whether the first `count` consecutive numbers from that
 * level's base are all conflict-free; any single conflict jumps to the next
 * depth. Stops (→ Fallback) if the base would exceed 999999 (6-digit cap).
 */
// eslint-disable-next-line max-params
function familyExpansion(
  reference: number,
  upper: number,
  count: number,
  existing: ReadonlySet<number>,
  maxOwned: number
): number[] {
  const refLen = String(reference).length;
  const upperLen = String(upper).length;

  for (let depth = 1; ; depth++) {
    const zeros = upperLen - refLen + depth;
    const base = parseInt(String(reference) + '0'.repeat(zeros), 10);

    if (base > 999999) break; // 6-digit cap exceeded — use Fallback.

    // The exact block of `count` consecutive candidates from base.
    const candidates = Array.from({ length: count }, (_, i) => base + i);

    // Accept only if the entire block is conflict-free — no gap-filling.
    if (candidates.every((n) => !existing.has(n))) return candidates;
    // Any conflict → jump to next depth.
  }

  return fallback(count, existing, maxOwned);
}

/**
 * Increments from the largest existing node number + 1, skipping conflicts.
 * Used when no reference exists, or when Family Expansion hits the 6-digit cap.
 */
function fallback(
  count: number,
  existing: ReadonlySet<number>,
  maxOwned: number
): number[] {
  const candidates: number[] = [];
  // PSTUDIO-4083: the anchor is `maxOwned`, computed by `assignNodeNumbers` over
  // the numbers rows HOLD — deliberately not `max(existing)`, which now also
  // contains every number a row merely points at. Counting from a reference
  // would let one dangling `error~99990` decide where this solution's numbering
  // starts. `existing` is still what a candidate is checked AGAINST, so a
  // referenced number is skipped rather than handed out.
  let next = maxOwned + 1;

  while (candidates.length < count) {
    // PSTUDIO-3992: `next++` no longer advances past Number.MAX_SAFE_INTEGER, so
    // an existing node numbered at the ceiling would hang this loop. A table
    // holds finitely many numbers, so a free positive integer always exists
    // below the maximum — scan up from 1 instead of spinning.
    if (next > Number.MAX_SAFE_INTEGER)
      return lowestFreeNumbers(count, existing);
    if (!existing.has(next)) candidates.push(next);
    next++;
  }

  return candidates;
}

/**
 * Last-resort allocator: the first `count` free positive integers counting up
 * from 1. Only reached when the table already holds a node numbered at
 * Number.MAX_SAFE_INTEGER, where incrementing can no longer produce a new
 * number. Guaranteed to terminate — `existing` is finite.
 */
function lowestFreeNumbers(
  count: number,
  existing: ReadonlySet<number>
): number[] {
  const candidates: number[] = [];
  let n = 1;
  while (candidates.length < count) {
    if (!existing.has(n)) candidates.push(n);
    n++;
  }
  return candidates;
}
